import { useEffect, useState } from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { format, isValid } from "date-fns";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { SectionPaper } from "@/components/Paper";
import { SectionTitle } from "@/components/Title";
import { Divider } from "@/components/Divider";
import {
  Button,
  ButtonColorOption,
  ButtonVariantOption,
} from "@/components/Button";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { CTextFieldPassword } from "@/components/TextField/CTextFieldPassword";
import { ConfirmDialog } from "@/components/Dialog";
import { useFeatureFlags } from "@/configs/featureFlag";
import { useSansanConnection } from "@/features/sansanIntegration/hooks/useSansanConnection";
import {
  SansanApiKeyFormSchema,
  type SansanApiKeyFormData,
} from "@/features/sansanIntegration/schemas/apiKeyForm";
import { type SansanConnectionStatus } from "@/features/sansanIntegration/types";

const STATUS_LABEL: Record<SansanConnectionStatus, string> = {
  NOT_CONNECTED: "未連携",
  VALID: "連携中",
  INVALID: "APIキーが無効",
};

const STATUS_COLOR: Record<
  SansanConnectionStatus,
  "default" | "success" | "error"
> = {
  NOT_CONNECTED: "default",
  VALID: "success",
  INVALID: "error",
};

/** 1要素を1行として表示する。 */
const API_KEY_GUIDE_LINES = [
  "ご自身のSansanアカウントでAPIキーを発行して登録してください（Sansanの管理者から「API連携を利用できる権限」を付与されている必要があります）。",
  "接点には、ご自身のSansanアカウントで参照できる名刺だけが表示されます。",
];

/** 1要素を1行として表示する。差し替えの操作はないため、キーを発行し直した場合は解除してから登録し直してもらう。 */
const INVALID_API_KEY_GUIDE_LINES = [
  "APIキーが無効か、API利用の権限がありません。",
  "キーを発行し直した場合は、連携を解除してから新しいAPIキーで登録してください。",
  "権限については、Sansanの管理者に確認してください。",
];

/** バックエンド由来の日時文字列を整形する。不正な値は描画を守るため非表示（null）にする。 */
const formatDateTime = (value: string | null | undefined): string | null => {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  return isValid(date) ? format(date, "yyyy/MM/dd HH:mm") : null;
};

/**
 * アカウント情報画面のSansan連携セクション。
 * LaunchDarklyの`sansanIntegration`フラグが有効なときのみ表示する（段階リリースのため）。
 * Sansan APIキーはSansanのユーザーごとに発行されるため、ログイン中のユーザー本人の連携だけを登録・解除できる。
 * 連携中はAPIキーの入力欄を出さない（差し替えの操作はなく、APIキーを替える場合は解除してから登録し直す）。
 */
export const SansanConnectionSection: React.FC = () => {
  const { sansanIntegration } = useFeatureFlags();
  const {
    connection,
    connectionStatus,
    registerApiKey,
    isRegistering,
    disconnect,
    isDisconnecting,
  } = useSansanConnection({ enabled: sansanIntegration === true });
  const [isDisconnectConfirmOpen, setIsDisconnectConfirmOpen] = useState(false);

  const form = useForm<SansanApiKeyFormData>({
    mode: "onSubmit",
    defaultValues: { apiKey: "" },
    resolver: zodResolver(SansanApiKeyFormSchema),
  });
  const apiKeyInput = useWatch({ control: form.control, name: "apiKey" });

  // 連携中になったら入力値を消す（409で連携中に切り替わった場合も、解除後に前のキーが残らないようにする）
  useEffect(() => {
    if (connection) {
      form.reset();
    }
  }, [connection, form]);

  // 取得中・取得失敗（未取得）は連携の状態が分からないため、Salesforce連携セクションと同じく何も表示しない
  if (sansanIntegration !== true || connection === undefined) {
    return null;
  }

  const isConnected = connectionStatus !== "NOT_CONNECTED";
  const isBusy = isRegistering || isDisconnecting;

  const handleSubmit = async ({
    apiKey,
  }: SansanApiKeyFormData): Promise<void> => {
    await registerApiKey(apiKey);
  };

  const handleConfirmDisconnect = (): void => {
    setIsDisconnectConfirmOpen(false);
    void disconnect();
  };

  const connectedAt = formatDateTime(connection?.connectedAt);
  const invalidatedAt = formatDateTime(connection?.apiKeyInvalidatedAt);

  return (
    <SectionPaper>
      <SectionTitle>Sansan連携</SectionTitle>
      <Divider />
      <Box sx={{ m: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Chip
            label={STATUS_LABEL[connectionStatus]}
            color={STATUS_COLOR[connectionStatus]}
            size="small"
          />
        </Stack>

        {connectionStatus === "INVALID" && (
          <Box sx={{ mb: 2 }}>
            {INVALID_API_KEY_GUIDE_LINES.map((line) => (
              <Typography key={line} variant="body2" color="error">
                {line}
              </Typography>
            ))}
            {invalidatedAt && (
              <Typography variant="body2" color="error">
                失効を検知した日時: {invalidatedAt}
              </Typography>
            )}
          </Box>
        )}

        {connection && (
          <Stack spacing={0.5} sx={{ mb: 2 }}>
            <Typography variant="body2">
              連携ユーザー: {connection.sansanUserName}
            </Typography>
            {connectedAt && (
              <Typography variant="body2">
                連携開始日時: {connectedAt}
              </Typography>
            )}
          </Stack>
        )}

        {isConnected ? (
          <Button
            label="連携を解除する"
            variant={ButtonVariantOption.Outlined}
            color={ButtonColorOption.Error}
            size={SizingWrapperStyle.SMALL}
            disabled={isBusy}
            onClick={() => {
              setIsDisconnectConfirmOpen(true);
            }}
          />
        ) : (
          <>
            <Box sx={{ mb: 1 }}>
              {API_KEY_GUIDE_LINES.map((line) => (
                <Typography key={line} variant="body2" color="text.secondary">
                  {line}
                </Typography>
              ))}
            </Box>
            <Box sx={{ mb: 2 }}>
              <CTextFieldPassword
                control={form.control}
                name="apiKey"
                label="Sansan APIキー"
                autoComplete="off"
                size={SizingWrapperStyle.LARGE}
                disabled={isBusy}
              />
            </Box>
            <Button
              label="登録"
              variant={ButtonVariantOption.Contained}
              size={SizingWrapperStyle.SMALL}
              disabled={isBusy || apiKeyInput === ""}
              onClick={() => {
                void form.handleSubmit(handleSubmit)();
              }}
            />
          </>
        )}
      </Box>

      <ConfirmDialog
        open={isDisconnectConfirmOpen}
        title="Sansan連携の解除"
        message={
          "Sansanとの連携を解除し、登録したAPIキーと、同期した名刺・接点の情報を削除します。\n再度連携すると、名刺の情報は登録後に改めて同期されます。\nよろしいですか？"
        }
        confirmLabel="解除する"
        confirmButtonColorOption={ButtonColorOption.Error}
        onConfirm={handleConfirmDisconnect}
        onCancel={() => {
          setIsDisconnectConfirmOpen(false);
        }}
      />
    </SectionPaper>
  );
};
